import { Button, SelectInput, SelectInputItem, TextField } from '@ads/components-react';
import { useForm } from '@tanstack/react-form';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { RequiredFieldsToCreateSlide } from '@/features/slide-editor/hooks/useSlideLibrary';
import type { ESSOCategoryEnum } from '@/shared/api/generated';
import type { SlideCategory } from '../../../store/atoms/slideCategoriesAtom';

// ─── Schemas ──────────────────────────────────────────────────────────────────
// Replicam as constraints do schema gerado (createSSOImageViewModelSchema):
// title: z.string().min(1).max(250) | dateStart/dateEnd: z.string()

const titleSchema = z.string().min(1).max(250);
const dateSchema = z.string().min(1);

// ─── Props ────────────────────────────────────────────────────────────────────

export interface CreateSlideModalProps {
  readonly isSaving: boolean;
  readonly categories: SlideCategory[];
  readonly onConfirm: ({ ...props }: RequiredFieldsToCreateSlide) => void;
  readonly onCancel: () => void;
}

/**
 * Modal para criação de um novo slide.
 * Permite informar título, categoria e período de exibição.
 * Fecha ao pressionar Escape ou clicar no overlay.
 */
export function CreateSlideModal({
  categories,
  onConfirm,
  onCancel,
  isSaving,
}: CreateSlideModalProps) {
  const { t } = useTranslation('slideEditor');
  const inputRef = useRef<HTMLInputElement>(null);
  const hasCategories = categories.length > 0;

  const form = useForm({
    defaultValues: {
      title: '',
      categoryId: categories[0]?.id ?? 0,
      dateStart: '',
      dateEnd: '',
    },
    onSubmit: ({ value }) => {
      onConfirm({
        title: value.title,
        categoryId: value.categoryId as ESSOCategoryEnum,
        dateStart: value.dateStart,
        dateEnd: value.dateEnd,
      });
    },
  });

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop overlay — click/keydown intentionally close the dialog
    <div
      className="fixed inset-none z-[999] flex items-center justify-center bg-base-neutral-black/deep"
      role="presentation"
      onClick={e => {
        if (e.target === e.currentTarget) onCancel();
      }}
      onKeyDown={e => {
        if (e.key === 'Escape') onCancel();
      }}
    >
      <div
        className="w-[90%] max-w-[28rem] rounded-md border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs shadow-level3"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="mb-xxs flex items-center justify-between">
          <span className="text-ctx-content-title ts-heading-sm">
            {t('library.createSlideModal.title')}
          </span>
          <button
            type="button"
            className="text-ctx-content-placeholder ts-caption-xxs hover:text-ctx-content-base"
            onClick={onCancel}
          >
            ✕
          </button>
        </div>

        <form
          onSubmit={e => {
            e.preventDefault();
            void form.handleSubmit();
          }}
          noValidate
          className="flex flex-col gap-nano"
        >
          {/* Título */}
          <form.Field name="title" validators={{ onChange: titleSchema }}>
            {field => (
              <TextField
                ref={inputRef}
                type="text"
                fullWidth
                label={t('library.createSlideModal.titleLabel')}
                size="md"
                placeholder={t('library.createSlideModal.titlePlaceholder')}
                value={field.state.value}
                onChange={e => field.handleChange(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Escape') onCancel();
                }}
                hasError={field.state.meta.errors.length > 0}
                disabled={isSaving}
              />
            )}
          </form.Field>

          {/* Categoria */}
          <div className="flex flex-col gap-quark">
            {hasCategories ? (
              <form.Field name="categoryId">
                {field => (
                  <SelectInput
                    label={t('library.createSlideModal.categoryLabel')}
                    size="md"
                    value={field.state.value.toString()}
                    onValueChange={value => field.handleChange(Number(value))}
                    fullWidth
                    custom={{
                      content: 'z-[10000]',
                    }}
                  >
                    {categories.map(cat => (
                      <SelectInputItem key={cat.id} value={cat.id.toString()}>
                        {cat.name}
                      </SelectInputItem>
                    ))}
                  </SelectInput>
                )}
              </form.Field>
            ) : (
              <p className="text-ctx-content-placeholder ts-caption-xxs">
                {t('library.createSlideModal.noCategories')}
              </p>
            )}
          </div>

          {/* Datas */}
          <div className="flex items-center gap-nano">
            <form.Field name="dateStart" validators={{ onChange: dateSchema }}>
              {field => (
                <TextField
                  type="date"
                  fullWidth
                  label={t('library.createSlideModal.dateStart')}
                  size="md"
                  value={field.state.value}
                  onChange={e => field.handleChange(e.target.value)}
                  hasError={field.state.meta.errors.length > 0}
                  disabled={isSaving}
                />
              )}
            </form.Field>
            <form.Field name="dateEnd" validators={{ onChange: dateSchema }}>
              {field => (
                <TextField
                  type="date"
                  fullWidth
                  label={t('library.createSlideModal.dateEnd')}
                  size="md"
                  value={field.state.value}
                  onChange={e => field.handleChange(e.target.value)}
                  hasError={field.state.meta.errors.length > 0}
                  disabled={isSaving}
                />
              )}
            </form.Field>
          </div>

          {/* Actions */}
          <div className="mt-xxs flex justify-end gap-nano">
            <Button hierarchy="secondary" size="md" type="button" onClick={onCancel}>
              {t('library.createModal.cancel')}
            </Button>
            <form.Subscribe selector={state => [state.values, state.isSubmitting] as const}>
              {([values, isSubmitting]) => {
                const isDisabled =
                  !values.title.trim() ||
                  !values.categoryId ||
                  !values.dateStart ||
                  !values.dateEnd;

                return (
                  <Button
                    type="submit"
                    isLoading={isSaving}
                    hierarchy="primary"
                    size="md"
                    disabled={isDisabled || isSaving || !!isSubmitting}
                  >
                    {t('library.createModal.confirm')}
                  </Button>
                );
              }}
            </form.Subscribe>
          </div>
        </form>
      </div>
    </div>
  );
}
